import { useEffect, useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { LifeBuoy, Send, ChevronDown } from 'lucide-react';
import { db, logAnalyticsEvent } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';

// Answers to the questions sellers ask most often, so they aren't blocked
// waiting on a reply. Keep each answer true to how the product behaves.
const FAQS: Array<{ q: string; a: string }> = [
  {
    q: 'What happens after a business buys a site from me?',
    a: 'They pay through a secure Stripe checkout that goes straight to your payout account. They then get a setup page where they add their contact details and download the complete website files, with a step-by-step guide to put it live for free on Netlify or Vercel. The website is theirs to keep.',
  },
  {
    q: 'Can the business use their own domain?',
    a: 'Yes. Netlify and Vercel both let them connect a domain they own, with free HTTPS.',
  },
  {
    q: 'If I cancel Romulus or delete my account, does their site stay online?',
    a: "Yes. A purchased site runs from the files the business downloaded and hosts themselves, so it doesn't depend on your Romulus account. Deleting your account removes your own previews and leads from Romulus.",
  },
  {
    q: 'Which countries are supported?',
    a: 'Leads: any country. Pick it when you create a campaign, or turn on Local Campaign to target a specific city. Payouts: any country Stripe supports. Choose yours in Settings → Payouts.',
  },
  {
    q: 'My payout account is set to the wrong country. How do I fix it?',
    a: "Open Settings → Payouts, choose the right country and continue. As long as your payout account isn't live yet, Romulus moves it to that country automatically.",
  },
  {
    q: 'Can I get paid without Stripe, for example by bank transfer?',
    a: 'Not at the moment. Site purchases are processed through Stripe, which pays out directly to your bank account.',
  },
  {
    q: 'A lead already has a website. What do I do?',
    a: "Click \"Has a website\" on that lead. It's removed from your list and the lead credit is returned to your quota.",
  },
  {
    q: "Is it okay to use a business's name and photos in a demo site?",
    a: "Demo sites are built from the business's public Google listing and shared with a private link, so only people you send it to can see it. Offer each site only to the business it was made for. This isn't legal advice. If you're unsure about the rules where you are, check with a local professional.",
  },
];

export function SupportPage() {
  const { user, userData } = useAuth();
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prefill email from the authenticated account on mount.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('You must be signed in to send a support message');
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail || !trimmedMessage) {
      toast.error('Email and message are both required');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'support-tickets'), {
        userId: user.uid,
        email: trimmedEmail,
        subject: subject.trim() || null,
        message: trimmedMessage,
        status: 'open',
        planId: userData?.planId || null,
        pageAtSubmit: window.location.pathname,
        userAgent: navigator.userAgent || null,
        createdAt: serverTimestamp(),
      });
      logAnalyticsEvent('support_ticket_submitted', {
        has_subject: !!subject.trim(),
      });
      toast.success("Message sent. We'll get back to you within 24 hours.");
      setSubject('');
      setMessage('');
    } catch (err) {
      console.error('Support submit failed:', err);
      toast.error('Failed to send message. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Support</h1>
        <p className="text-gray-400 mt-1">Get help from the team</p>
      </div>

      <GlassCard>
        <h2 className="text-lg font-semibold text-white mb-4">Common questions</h2>
        <div className="divide-y divide-white/10">
          {FAQS.map((faq) => (
            <details key={faq.q} className="group py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-white">
                {faq.q}
                <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-500 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </GlassCard>

      <GlassCard>
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
            <LifeBuoy className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Send us a message</h2>
            <p className="text-sm text-gray-400 mt-1">
              Bug, feature request, billing question, whatever. We read every message
              and usually reply within 24 hours.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supportEmail">Your email</Label>
            <Input
              id="supportEmail"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-gray-500">We'll reply here.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supportSubject">Subject (optional)</Label>
            <Input
              id="supportSubject"
              type="text"
              placeholder="Short summary"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="supportMessage">Message</Label>
            <textarea
              id="supportMessage"
              rows={8}
              placeholder="What's going on? The more detail the better. Include steps to reproduce, error messages, etc."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-y min-h-[160px]"
            />
            <p className="text-xs text-gray-500">
              {message.length.toLocaleString()} / 5,000 characters
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting || !email.trim() || !message.trim()}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              Send message
            </Button>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
